import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { ATTENTION } from '../mock';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AppContext = createContext(null);

export const AppProvider = ({ children }) => {
  const [pet, setPet] = useState(null);
  const [memories, setMemories] = useState([]);
  const [ring, setRing] = useState(null);
  const [recall, setRecall] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const [petRes, memRes, ringRes, recallRes] = await Promise.all([
        axios.get(`${API}/pet`),
        axios.get(`${API}/memory-events`),
        axios.get(`${API}/integrations/ring/status`),
        axios.get(`${API}/recall/walk-today`),
      ]);
      setPet(petRes.data || null);
      setMemories(Array.isArray(memRes.data) ? memRes.data : []);
      setRing(ringRes.data || null);
      setRecall(recallRes.data || null);
    } catch (e) {
      console.error('Failed to load app data', e);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const fetchMemory = useCallback(async (id) => {
    const res = await axios.get(`${API}/memory-events/${id}`);
    return res.data;
  }, []);

  const confirmMemory = useCallback(
    async (id, classification) => {
      const res = await axios.post(`${API}/memory-events/${id}/confirm`, {
        classification,
      });
      await loadAll();
      return res.data;
    },
    [loadAll]
  );

  const dismissMemory = useCallback(
    async (id) => {
      await axios.post(`${API}/memory-events/${id}/dismiss`);
      await loadAll();
    },
    [loadAll]
  );

  const resetState = useCallback(async () => {
    await axios.post(`${API}/dev/reset`);
    await loadAll();
  }, [loadAll]);

  const getMemory = useCallback(
    (id) => memories.find((m) => m.id === id),
    [memories]
  );

  const value = {
    loading,
    error,
    pet,
    attention: ATTENTION,
    ring,
    recall,
    memories,
    getMemory,
    fetchMemory,
    confirmMemory,
    dismissMemory,
    resetState,
    reload: loadAll,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export const useApp = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within AppProvider');
  return ctx;
};
